import { registerLocaleData } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeNo from '@angular/common/locales/nb';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  RouteReuseStrategy,
  TitleStrategy,
  withRouterConfig,
} from '@angular/router';
import { provideTranslateService, TranslateCompiler, TranslateLoader } from '@ngx-translate/core';
import { IntercomModule } from '@supy-io/ngx-intercom';
import { Angulartics2Module } from 'angulartics2';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/nb';
import { resolveAppLocale } from '@shared/utils/app-locale';
import { TemplatePageTitleStrategy } from './app-title-strategy';
import { AppRoutes } from './app.routes';
import { TranslateLoaderFactory } from './app.translations';
import { LanguageService } from './core/providers/backend/language.service';
import { ContextRouteReuseStrategy } from './core/providers/context/context-route-reuse.strategy';
import { ChunkReloadErrorHandler } from './core/providers/error-handler/chunk-reload-error-handler';
import { UnitTypeTranslateCompiler } from './core/providers/translator/unit-type-translate-compiler';
import { FBI_MOMENT_DATE_FORMATS } from './fbi-date-formats';
import { fbiInterceptorFn } from './fbi.interceptor';

registerLocaleData(localeNo);
registerLocaleData(localeEn);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    provideRouter(
      AppRoutes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      })
    ),
    { provide: RouteReuseStrategy, useClass: ContextRouteReuseStrategy },
    provideHttpClient(withInterceptors([fbiInterceptorFn])),
    provideMomentDateAdapter(),

    { provide: ErrorHandler, useClass: ChunkReloadErrorHandler },

    { provide: MAT_DATE_LOCALE, useValue: 'nb' },
    { provide: MAT_DATE_FORMATS, useValue: FBI_MOMENT_DATE_FORMATS },
    {
      provide: LOCALE_ID,
      useFactory: resolveAppLocale,
    },
    { provide: TitleStrategy, useClass: TemplatePageTitleStrategy },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_CARD_CONFIG, useValue: { appearance: 'outlined' } },
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-icons-round' },
    },
    // {
    //   provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
    //   useValue: { verticalPosition: 'top', horizontalPosition: 'center' },
    // },

    LanguageService,
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslateLoaderFactory,
        deps: [HttpClient],
      },
      compiler: { provide: TranslateCompiler, useClass: UnitTypeTranslateCompiler },
      fallbackLang: 'en-GB',
    }),
    importProvidersFrom(
      IntercomModule.forRoot({
        appId: '',
        updateOnRouterChange: true,
      }),
      Angulartics2Module.forRoot({
        pageTracking: {
          clearIds: true,
          clearQueryParams: true,
        },
      })
    ),
  ],
};
